import { Link } from '@tanstack/react-router'
import Header from '@/components/layouts/Header'
import Footer from '@/components/layouts/Footer'
import { Button } from '@/components/ui/button'

interface MarketingSection {
  title: string
  description: string
  bullets: string[]
}

interface MarketingPageProps {
  badge: string
  title: string
  subtitle: string
  ctaLabel: string
  sections: MarketingSection[]
  faqTitle: string
  faqs: Array<{ question: string; answer: string }>
}

export function MarketingPage({
  badge,
  title,
  subtitle,
  ctaLabel,
  sections,
  faqTitle,
  faqs,
}: MarketingPageProps) {
  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <Header />
      <main className="flex-1">
        <section className="mx-auto w-full max-w-5xl px-4 pb-16 pt-14 md:px-6">
          <div className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary w-fit">
            {badge}
          </div>
          <h1 className="mt-5 font-serif text-4xl font-bold leading-tight md:text-5xl">
            {title}
          </h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">{subtitle}</p>
          <div className="mt-7">
            <Button asChild size="lg">
              <Link to="/register">{ctaLabel}</Link>
            </Button>
          </div>
        </section>

        <section className="border-y bg-muted/30 py-14">
          <div className="mx-auto grid w-full max-w-5xl gap-5 px-4 md:grid-cols-2 md:px-6">
            {sections.map((section) => (
              <article key={section.title} className="rounded-2xl border bg-card p-6 shadow-sm">
                <h2 className="text-xl font-semibold">{section.title}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{section.description}</p>
                <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-foreground/90">
                  {section.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-5xl px-4 py-14 md:px-6">
          <h2 className="font-serif text-3xl font-bold">{faqTitle}</h2>
          <div className="mt-6 space-y-5">
            {faqs.map((faq) => (
              <article key={faq.question} className="rounded-xl border p-5">
                <h3 className="text-lg font-semibold">{faq.question}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{faq.answer}</p>
              </article>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}
